import commons from '@/utils/Common'
import ApiService from '@/services/servicesApi'
import { WALLET_V1_POINTS_BALANCE } from '@/api/const/wallet'

const STORAGE_BASE = 'loyalty_points_balance'
const EVENT = 'loyalty-points-changed'

export type LoyaltyTierId = 'bronze' | 'silver' | 'gold' | 'diamond'

export type LoyaltySnapshot = {
  balance: number
  tierId: LoyaltyTierId
}

const TIER_THRESHOLDS: { id: LoyaltyTierId; need: number }[] = [
  { id: 'bronze', need: 0 },
  { id: 'silver', need: 5000 },
  { id: 'gold', need: 15000 },
  { id: 'diamond', need: 40000 },
]

const asUserRecord = (raw: unknown): Record<string, any> => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, any>
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, any>
      }
    } catch {
      /* ignore invalid cookie payload */
    }
  }
  return {}
}

const userScope = () => {
  const user = asUserRecord(commons.getUserData?.())
  return user.id || user.userId || user.email || 'anon'
}

const storageKey = () => `${commons.tenantKey(STORAGE_BASE)}_${userScope()}`

const readRaw = (): number | null => {
  try {
    const raw = localStorage.getItem(storageKey())
    if (raw == null || raw === '') return null
    const n = Number(raw)
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : null
  } catch {
    return null
  }
}

const writeRaw = (balance: number) => {
  const next = Math.max(0, Math.floor(balance))
  try {
    localStorage.setItem(storageKey(), String(next))
  } catch {
    /* ignore quota */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(EVENT, { detail: { balance: next } as LoyaltySnapshot })
    )
  }
  return next
}

/** Cache số dư của engine; 0 khi chưa fetch được (không seed số giả). */
export const getLoyaltyBalance = (): number => {
  if (!commons.checkLogin?.()) return 0
  return readRaw() ?? 0
}

export const setLoyaltyBalance = (balance: number): number => writeRaw(balance)

export const addLoyaltyPoints = (delta: number): number => {
  const current = getLoyaltyBalance()
  return writeRaw(current + delta)
}

/** SSE / API payload → cập nhật chip header bằng số dư tuyệt đối nếu có. */
export const applyRemoteLoyaltyBalance = (payload: Record<string, any> | null | undefined): number | null => {
  if (!payload) return null
  const abs = Number(payload.balance ?? payload.balanceAfter ?? payload.availableBalance)
  if (Number.isFinite(abs) && abs >= 0) {
    return setLoyaltyBalance(abs)
  }
  const amount = Number(payload.amount ?? payload.quantity)
  if (Number.isFinite(amount) && amount !== 0) {
    return addLoyaltyPoints(amount)
  }
  return null
}

export const resolveLoyaltyUserIds = (): { tenantId: number | null; userId: number | null } => {
  const user = asUserRecord(commons.getUserData?.())
  const userId = toId(user.id ?? user.userId ?? user.user_id)
  const tenantId = toId(user.tenantId ?? user.tenant_id ?? user.tenantsId) ?? 1
  return { tenantId, userId }
}

const toId = (value: unknown): number | null => {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null
}

/** Đọc số dư thật từ wallet-engine qua BE proxy; null nếu không lấy được. */
export const fetchLoyaltyBalance = async (): Promise<number | null> => {
  if (!commons.checkLogin?.()) return null
  const { tenantId, userId } = resolveLoyaltyUserIds()
  if (tenantId == null || userId == null) return null
  try {
    ApiService.init()
    const res: any = await ApiService.getNoParam(
      `${WALLET_V1_POINTS_BALANCE}?tenantId=${tenantId}&userId=${userId}`
    )
    const data = res?.data?.data ?? res?.data
    if (!data || typeof data !== 'object') return null
    return applyRemoteLoyaltyBalance(data as Record<string, any>)
  } catch {
    return null
  }
}

export const getLoyaltyTierId = (balance = getLoyaltyBalance()): LoyaltyTierId => {
  let current: LoyaltyTierId = 'bronze'
  for (const tier of TIER_THRESHOLDS) {
    if (balance >= tier.need) current = tier.id
  }
  return current
}

export const getLoyaltySnapshot = (): LoyaltySnapshot => {
  const balance = getLoyaltyBalance()
  return { balance, tierId: getLoyaltyTierId(balance) }
}

/** Compact for header chip: 12850 → 12.9k */
export const formatLoyaltyCompact = (balance: number): string => {
  const n = Math.max(0, Math.floor(balance))
  if (n < 1000) return String(n)
  if (n < 10000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`
  if (n < 1000000) return `${Math.round(n / 1000)}k`
  return `${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`
}

export const formatLoyaltyFull = (balance: number, locale = 'vi-VN'): string =>
  Math.max(0, Math.floor(balance)).toLocaleString(locale)

export const onLoyaltyPointsChange = (handler: (snap: LoyaltySnapshot) => void) => {
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<LoyaltySnapshot>).detail
    if (detail && typeof detail.balance === 'number') {
      handler({
        balance: detail.balance,
        tierId: detail.tierId || getLoyaltyTierId(detail.balance),
      })
      return
    }
    handler(getLoyaltySnapshot())
  }
  window.addEventListener(EVENT, listener)
  return () => window.removeEventListener(EVENT, listener)
}
